// Wireless PTP link analysis sub-panel.
// Shows nearby towers, radio picker, LoS verdict, elevation profile chart.
// Slots into the expansion study flow or standalone tower-to-tower analysis.

import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';

type RadioSummary = {
  id: string; name: string; type: 'ptp' | 'ptmp';
  freq_ghz: number; max_throughput_mbps: number;
  max_range_km: number; price_per_link_usd: number;
  los_required: boolean; nlos_max_range_km: number;
};

type LinkResult = {
  distance_m: number; distance_mi: number;
  fspl_db: number; link_margin_db: number;
  los_clear: boolean; fresnel1_clear_pct: number;
  max_obstruction_m: number; verdict: 'green' | 'yellow' | 'red';
  estimated_throughput_mbps: number;
  radio: any; radio_cost_usd: number;
  profile: Array<{
    distance_m: number; ground_elevation_m: number;
    los_height_m: number; fresnel_upper_m: number; fresnel_lower_m: number;
  }>;
  warnings: string[];
};

const fmtUSD = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M`
  : n >= 1_000   ? `$${(n / 1_000).toFixed(0)}k`
  : `$${Math.round(n).toLocaleString()}`;

export default function LinkAnalysisPanel({
  towerA,
  towerB,
  onResult,
}: {
  towerA: { lat: number; lng: number; height_m: number; label?: string } | null;
  towerB: { lat: number; lng: number; height_m: number; label?: string } | null;
  onResult?: (result: LinkResult | null) => void;
}) {
  const [radioId, setRadioId] = useState('cambium_ptp670');
  const [heightA, setHeightA] = useState(towerA?.height_m ?? 30);
  const [heightB, setHeightB] = useState(towerB?.height_m ?? 30);

  useEffect(() => { if (towerA) setHeightA(towerA.height_m); }, [towerA?.height_m]);
  useEffect(() => { if (towerB) setHeightB(towerB.height_m); }, [towerB?.height_m]);

  const { data: radios = [] } = useQuery<RadioSummary[]>({
    queryKey: ['radios'],
    queryFn: () => api.get('/map/radios'),
    staleTime: 300_000,
  });

  const analyzeMut = useMutation({
    mutationFn: () => {
      if (!towerA || !towerB) return Promise.reject(new Error('Select two towers'));
      return api.post('/map/link-analysis', {
        towerA: { lat: towerA.lat, lng: towerA.lng, height_m: heightA },
        towerB: { lat: towerB.lat, lng: towerB.lng, height_m: heightB },
        radio_id: radioId,
      });
    },
    onSuccess: (res: LinkResult) => onResult?.(res),
  });

  const result = analyzeMut.data as LinkResult | undefined;
  const selectedRadio = radios.find((r) => r.id === radioId);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '.08em' }}>
        Wireless PTP Link Analysis
      </div>

      {/* Tower endpoints */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <TowerCard
          label="Tower A"
          tower={towerA}
          height={heightA}
          onHeightChange={setHeightA}
        />
        <TowerCard
          label="Tower B"
          tower={towerB}
          height={heightB}
          onHeightChange={setHeightB}
        />
      </div>

      {/* Radio picker */}
      <div>
        <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Radio</label>
        <select
          value={radioId}
          onChange={(e) => setRadioId(e.target.value)}
          style={{ width: '100%', padding: '6px 8px', fontSize: 12 }}
        >
          {radios.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name} — {r.max_throughput_mbps} Mbps, {fmtUSD(r.price_per_link_usd)}/link
              {r.max_range_km < 10 ? `, max ${r.max_range_km} km` : ''}
            </option>
          ))}
        </select>
        {selectedRadio && (
          <div style={{ fontSize: 10, color: 'var(--text-subtle)', marginTop: 3 }}>
            {selectedRadio.freq_ghz} GHz · {selectedRadio.type.toUpperCase()}
            {!selectedRadio.los_required && ` · NLOS up to ${selectedRadio.nlos_max_range_km} km`}
          </div>
        )}
      </div>

      {/* Analyze button */}
      <button
        className="btn btn--primary"
        onClick={() => analyzeMut.mutate()}
        disabled={!towerA || !towerB || analyzeMut.isPending}
        style={{ width: '100%' }}
      >
        {analyzeMut.isPending ? 'Analyzing link…' : '📡 Check link feasibility'}
      </button>

      {analyzeMut.isError && (
        <div style={{ color: '#dc2626', fontSize: 12 }}>
          {(analyzeMut.error as Error)?.message ?? 'Analysis failed'}
        </div>
      )}

      {/* Result */}
      {result && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Verdict banner */}
          <div style={{
            padding: 10, borderRadius: 6, textAlign: 'center',
            background: result.verdict === 'green' ? '#ecfdf5' : result.verdict === 'yellow' ? '#fffbeb' : '#fef2f2',
            border: `2px solid ${result.verdict === 'green' ? '#10b981' : result.verdict === 'yellow' ? '#f59e0b' : '#ef4444'}`,
          }}>
            <div style={{
              fontSize: 20, fontWeight: 800,
              color: result.verdict === 'green' ? '#065f46' : result.verdict === 'yellow' ? '#92400e' : '#991b1b',
            }}>
              {result.verdict === 'green' ? '✓ Link viable' :
               result.verdict === 'yellow' ? '⚠ Marginal' :
               '✗ Link blocked'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              {result.distance_mi} mi · {result.estimated_throughput_mbps} Mbps est. · Fresnel {result.fresnel1_clear_pct}% clear
            </div>
          </div>

          {/* Metrics */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            <Metric label="Distance" value={`${result.distance_mi} mi`} />
            <Metric label="Throughput" value={`${result.estimated_throughput_mbps} Mbps`}
                    color={result.estimated_throughput_mbps > 200 ? '#10b981' : '#f59e0b'} />
            <Metric label="Path loss" value={`${result.fspl_db} dB`} />
            <Metric label="Link margin" value={`${result.link_margin_db} dB`}
                    color={result.link_margin_db > 10 ? '#10b981' : result.link_margin_db > 0 ? '#f59e0b' : '#ef4444'} />
            <Metric label="Fresnel clear" value={`${result.fresnel1_clear_pct}%`}
                    color={result.fresnel1_clear_pct >= 60 ? '#10b981' : '#f59e0b'} />
            <Metric label="Radio cost" value={fmtUSD(result.radio_cost_usd)} />
          </div>

          {/* Elevation profile */}
          {result.profile.length > 0 && (
            <ElevationChart profile={result.profile} verdict={result.verdict} />
          )}

          {/* Warnings */}
          {result.warnings.length > 0 && (
            <div style={{ fontSize: 11, color: '#92400e', background: '#fffbeb', padding: 6, borderRadius: 4 }}>
              {result.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TowerCard({
  label, tower, height, onHeightChange,
}: {
  label: string;
  tower: { lat: number; lng: number; height_m: number; label?: string } | null;
  height: number;
  onHeightChange: (h: number) => void;
}) {
  if (!tower) {
    return (
      <div style={{ padding: 8, background: '#f1f5f9', borderRadius: 6, fontSize: 11, color: 'var(--text-muted)' }}>
        {label}: click a tower on the map
      </div>
    );
  }
  return (
    <div style={{ padding: 8, background: '#f0f9ff', border: '1px solid #93c5fd', borderRadius: 6 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#1e40af' }}>{label}</div>
      <div style={{ fontSize: 11 }}>{tower.label ?? `${tower.lat.toFixed(4)}, ${tower.lng.toFixed(4)}`}</div>
      <label style={{ fontSize: 10, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
        Height (m):
        <input
          type="number" min={5} max={500} step={5} value={height}
          onChange={(e) => onHeightChange(Number(e.target.value))}
          style={{ width: 60, padding: '2px 4px', fontSize: 11 }}
        />
      </label>
    </div>
  );
}

function Metric({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ background: '#f8fafc', border: '1px solid var(--border)', borderRadius: 4, padding: '5px 8px' }}>
      <div style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: color ?? 'var(--text)' }}>{value}</div>
    </div>
  );
}

// Simple SVG elevation profile chart
function ElevationChart({
  profile,
  verdict,
}: {
  profile: LinkResult['profile'];
  verdict: 'green' | 'yellow' | 'red';
}) {
  const W = 280, H = 120, PAD = 20;
  const maxD = Math.max(...profile.map((p) => p.distance_m)) || 1;
  const allEls = profile.flatMap((p) => [p.ground_elevation_m, p.los_height_m, p.fresnel_upper_m]);
  const minEl = Math.min(...allEls) - 10;
  const maxEl = Math.max(...allEls) + 10;

  const x = (d: number) => PAD + (d / maxD) * (W - 2 * PAD);
  const y = (el: number) => H - PAD - ((el - minEl) / (maxEl - minEl)) * (H - 2 * PAD);

  const groundPath = profile.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p.distance_m).toFixed(1)},${y(p.ground_elevation_m).toFixed(1)}`).join(' ');
  const losPath = `M${x(profile[0].distance_m).toFixed(1)},${y(profile[0].los_height_m).toFixed(1)} L${x(profile[profile.length - 1].distance_m).toFixed(1)},${y(profile[profile.length - 1].los_height_m).toFixed(1)}`;

  // Fresnel zone as a filled area between upper and lower
  const fresnelUpper = profile.map((p) => `${x(p.distance_m).toFixed(1)},${y(p.fresnel_upper_m).toFixed(1)}`).join(' L');
  const fresnelLower = [...profile].reverse().map((p) => `${x(p.distance_m).toFixed(1)},${y(p.fresnel_lower_m).toFixed(1)}`).join(' L');
  const fresnelPath = `M${fresnelUpper} L${fresnelLower} Z`;

  const fresnelColor = verdict === 'green' ? 'rgba(16,185,129,0.15)' : verdict === 'yellow' ? 'rgba(245,158,11,0.15)' : 'rgba(239,68,68,0.15)';
  const fresnelStroke = verdict === 'green' ? '#10b981' : verdict === 'yellow' ? '#f59e0b' : '#ef4444';

  return (
    <div style={{ background: '#0b1220', borderRadius: 6, padding: 8 }}>
      <div style={{ fontSize: 9, color: '#64748b', marginBottom: 4 }}>Elevation profile + Fresnel zone 1</div>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
        {/* Fresnel zone fill */}
        <path d={fresnelPath} fill={fresnelColor} />
        {/* Ground */}
        <path d={groundPath} fill="none" stroke="#64748b" strokeWidth={1.5} />
        {/* Ground fill to bottom */}
        <path d={`${groundPath} L${x(maxD).toFixed(1)},${H - PAD} L${PAD},${H - PAD} Z`} fill="rgba(100,116,139,0.2)" />
        {/* LoS line */}
        <path d={losPath} fill="none" stroke={fresnelStroke} strokeWidth={1} strokeDasharray="4,3" />
        {/* Tower markers */}
        <circle cx={x(0)} cy={y(profile[0].los_height_m)} r={4} fill="#dc2626" stroke="#fff" strokeWidth={1.5} />
        <circle cx={x(maxD)} cy={y(profile[profile.length - 1].los_height_m)} r={4} fill="#dc2626" stroke="#fff" strokeWidth={1.5} />
      </svg>
    </div>
  );
}
